import { Navigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/shared/hooks/useAuth';
import { HOME_ROUTE } from '@/shared/constants/routes';
import { supabase } from '@/shared/lib/supabase';
import { TwoFactorSetup } from '@/shared/components/TwoFactorSetup';
import { AuthLoading } from './AuthLoading';
import { clearStaffEnrolmentRequired, useStaffEnrolmentStore } from '@/shared/hooks/useAuthMfa';

/**
 * Staff must use two-step verification: the API refuses every console call
 * from a staff token below aal2 (403 MFA_ENROLMENT_REQUIRED, apps/api
 * SuperAdminGuard). The set-up is offered here, once, before the console
 * (it is also in Settings → Security, which staff can open like anyone).
 */
function StaffMfaEnrolment(): JSX.Element {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  return (
    <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center p-5">
      <div className="w-[min(560px,100%)] space-y-4">
        <h1 className="font-display text-[24px] leading-tight m-0 text-[var(--color-ink)]">{t('auth.staffMfa.title')}</h1>
        <p className="text-body leading-[1.5] text-[var(--color-ink-2)] m-0">{t('auth.staffMfa.body')}</p>
        <TwoFactorSetup
          allowDisable={false}
          // The verified factor raises this session to aal2; refreshing pulls
          // the user with the new factor so the guard opens the console.
          onEnabled={() => {
            clearStaffEnrolmentRequired();
            void supabase.auth.refreshSession();
          }}
        />
        <button type="button" onClick={() => void signOut()} className="btn btn-secondary">
          {t('auth.staffMfa.signOut')}
        </button>
      </div>
    </div>
  );
}

export function SuperAdminGuard(): JSX.Element {
  const { user, isLoading } = useAuth();
  const enrolmentRequired = useStaffEnrolmentStore((s) => s.required);

  if (isLoading) return <AuthLoading />;

  const isSuperAdmin = user?.app_metadata?.role === 'super_admin';
  if (!isSuperAdmin) return <Navigate to={HOME_ROUTE} replace />;

  const hasFactor = (user?.factors ?? []).some((f) => f.status === 'verified');
  // The API's 403 MFA_ENROLMENT_REQUIRED wins over a stale local factor list.
  if (!hasFactor || enrolmentRequired) return <StaffMfaEnrolment />;

  return <Outlet />;
}
